import { CITY_MIN_DISPLAY_COUNT } from '../displays/cityThreshold.js';
import { filterDisplaysByParams, type FilterableDisplay } from '../displays/locationFilters.js';
import { holidayViewIndexable, indexableHolidayFilter, type IndexableHoliday } from '../displays/locationSeo.js';
import { districtCopy } from './paths.js';

/**
 * Season-aware copy and holiday views for a Light District page. The SPA and
 * api/seo-render both call districtViewSeo, so the title, H1, description,
 * canonical and robots decision cannot drift between the browser and the
 * crawler HTML. Dates are read in America/New_York so a server on UTC and a
 * visitor anywhere agree on which side of a boundary today falls.
 */

export type DistrictHolidayRow = Pick<FilterableDisplay, 'holidays' | 'holiday_type'>;
export type HolidayCounts = Record<IndexableHoliday, number>;

const NY_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

export function newYorkDate(date: Date): { year: number; month: number; day: number } {
  const parts = Object.fromEntries(NY_PARTS.formatToParts(date).map((p) => [p.type, Number(p.value)]));
  return { year: parts.year, month: parts.month, day: parts.day };
}

const holidayRows = (rows: readonly DistrictHolidayRow[], holiday: string) =>
  filterDisplaysByParams(rows as unknown as FilterableDisplay[], { holiday });

export const countDistrictHolidays = (rows: readonly DistrictHolidayRow[]): HolidayCounts => ({
  christmas: holidayRows(rows, 'christmas').length,
  halloween: holidayRows(rows, 'halloween').length,
});

/**
 * Which holiday the plain district page should talk about today.
 * Aug 15 - Oct 31: Halloween with enough Halloween displays, else Christmas
 * with enough Christmas displays. Nov 1 - Jan 6: Christmas with enough
 * Christmas displays. Otherwise (and off-season) null, meaning neutral wording.
 */
export function districtSeasonHoliday(date: Date, counts: HolidayCounts): IndexableHoliday | null {
  const { month, day } = newYorkDate(date);
  const halloweenSeason = (month === 8 && day >= 15) || month === 9 || month === 10;
  const christmasSeason = month >= 11 || (month === 1 && day <= 6);
  if (halloweenSeason && counts.halloween >= CITY_MIN_DISPLAY_COUNT) return 'halloween';
  if ((halloweenSeason || christmasSeason) && counts.christmas >= CITY_MIN_DISPLAY_COUNT) return 'christmas';
  return null;
}

type QueryLike = Parameters<typeof indexableHolidayFilter>[0];

export interface DistrictViewInput {
  district: { name: string; parentCity: string; state: string; displayCount: number; path: string };
  /** The district's displays (the API's 200-row projection), with holidays and holiday_type. */
  rows: readonly DistrictHolidayRow[];
  /** Visitor query string with route params already removed. */
  query: QueryLike;
  now: Date;
}

export function districtViewSeo({ district, rows, query, now }: DistrictViewInput) {
  const { year } = newYorkDate(now);
  const counts = countDistrictHolidays(rows);
  const requested = indexableHolidayFilter(query);
  const indexableView = requested !== null && holidayViewIndexable(counts[requested]) ? requested : null;
  const holiday = indexableView ?? districtSeasonHoliday(now, counts);
  const count = indexableView ? counts[indexableView] : holiday ? counts[holiday] : district.displayCount;
  const copy = districtCopy(district.name, district.parentCity, district.state, count, year, holiday);
  const hasQuery = (query instanceof URLSearchParams ? Array.from(query.keys()) : Object.keys(query)).length > 0;
  return {
    year,
    copy,
    /** Holiday the page wording is about (view, season default, or null for neutral). */
    holiday,
    /** The holiday view this URL is a self-canonical, indexable copy of, or null. */
    indexableView,
    /** Matching count for the wording (view/season) or the whole district. */
    count,
    canonicalPath: indexableView ? `${district.path}?holiday=${indexableView}` : district.path,
    robotsNoindex: hasQuery && !indexableView,
    counts,
    /** Holiday views worth linking to: same threshold as indexability. */
    linkedHolidays: (['halloween', 'christmas'] as const).filter((h) => holidayViewIndexable(counts[h])),
  };
}

/** Rows matching a holiday view (any ?holiday= value the city pages accept). */
export const filterDistrictRows = <T extends DistrictHolidayRow>(rows: readonly T[], holiday: string): T[] =>
  holiday.trim() ? (filterDisplaysByParams(rows as unknown as FilterableDisplay[], { holiday }) as unknown as T[]) : [...rows];
